import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { GroupSummaryStore, GroupType } from 'src/stores/groups-store';

const GroupsSelect: FC<
  {
    onChange: (values: { value: string; label: string; organization: string; organizationId: string; type: GroupType }[]) => void;
    exclude?: string[];
    values?: { value: string; label: string }[];
    organizationIds?: string[];
  } & WithTranslation
> = ({ t, onChange, exclude, values, organizationIds }) => {
  const groupStore = container.get(GroupSummaryStore);
  const groupsState = groupStore.state;
  const [currentValues, setValues] = useState((values || []).map((x) => ({ value: x.value, label: x.label || `Group ${x.value}` })));
  const [orgIds, setOrgIds] = useState(organizationIds);

  const load = (search: string | undefined = undefined) => {
    let filter = (orgIds || []).length === 0 ? [] : [`(OrganizationId eq ${orgIds?.join(' or OrganizationId eq ')})`];

    if ((exclude || []).length !== 0) filter = [...filter, `id ne ${exclude?.join(' and id ne ')}`];

    groupStore.loadWithTypes(
      {
        searchQuery: search,
        orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
        skip: 0,
        take: 1000000,
        filter,
      } as Query,
      [GroupType.Default]
    );
  };

  useEffect(() => {
    let nextValue = values || currentValues || [];
    if (nextValue && nextValue.some((x) => (exclude || []).includes(x.value))) nextValue = [];
    setValues((nextValue || []).map((x) => ({ value: x.value, label: x.label || `Group ${x.value}` })));
    load();
  }, [exclude, groupStore, values]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOrgIds(organizationIds);
    setValues([]);
    if (currentValues.length !== 0) {
      onChange && onChange([]);
    }
  }, [organizationIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (nextValues: { value: string; label: string }[], options: any) => {
    setValues(nextValues);
    onChange &&
      onChange(
        (options as any[]).map((x) => ({
          value: x.value,
          label: x.children,
          organization: x.organization,
          organizationId: x.organizationId,
          type: x.groupType,
        }))
      );
  };

  return (
    <Select
      mode="multiple"
      loading={!!groupsState.isBusy.get()}
      labelInValue
      placeholder={t('SelectGroups')}
      onChange={onSelect}
      style={{ width: '100%' }}
      allowClear
      showSearch
      value={currentValues}
      notFoundContent={groupsState.isBusy.get() ? <Spin size="small" /> : undefined}
      filterOption={false}
      showArrow={true}
      onSearch={onSearch}
      onDropdownVisibleChange={(visible) => {
        if (!visible) load();
      }}
    >
      {groupsState.items.value.map((o) => (
        <Select.Option label={o.name} key={o.id} value={o.id} organizationId={o.organizationId} organization={o.organization} groupType={o.groupType}>
          {o.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export default withTranslation()(GroupsSelect);
